import { useCallback, useEffect, useState } from "react";

import clsx from "clsx";
import { isEqual } from "lodash";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { useMetricsExplorerStateContext } from "@features/metrics-explorer/hooks";
import { type SavedView } from "@features/metrics-explorer/types";
import { SyncViewReordering } from "@features/saved-views/sync-view-reordering";
import {
  GET_METRICS_EXPLORER_SAVED_VIEWS,
  UPDATE_METRICS_EXPLORER_SAVED_VIEW_ORDERING,
  UPDATE_METRICS_EXPLORER_SAVED_VIEW_ORDERING_TO_ALL_ACCOUNT_MANAGERS,
} from "@gql/metrics-explorer-saved-views";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type UpdateMetricsExplorerSavedViewOrdering,
  type UpdateMetricsExplorerSavedViewOrderingVariables,
} from "@north-beam/nb-client-side";
import {
  type UpdateMetricsExplorerSavedViewOrderingToAllAccountManagers,
  type UpdateMetricsExplorerSavedViewOrderingToAllAccountManagersVariables,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { IconButton } from "@shared/buttons";
import Spinner from "@shared/spinner";

import { SavedViewsMenuRow } from "./saved-views-menu-row";

interface Props {
  isReordering: boolean;
  cancelReordering(): void;
  closeMenu(): void;
  isMenuOpen: boolean;
}

export const ReorderSavedViews = ({
  isReordering,
  cancelReordering,
  closeMenu,
  isMenuOpen,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const { savedViews, selectedSavedView, selectSavedView } =
    useMetricsExplorerStateContext();

  const [isSavedViewsReorderLoading, setIsSavedViewsReorderLoading] =
    useState(false);
  const [currentSavedViewOrder, setCurrentSavedViewOrder] = useState<
    SavedView[]
  >([]);

  useEffect(() => {
    setCurrentSavedViewOrder(savedViews);
  }, [savedViews]);

  const [updateSavedViewOrdering] = useNorthbeamMutation<
    UpdateMetricsExplorerSavedViewOrdering,
    UpdateMetricsExplorerSavedViewOrderingVariables
  >(UPDATE_METRICS_EXPLORER_SAVED_VIEW_ORDERING, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_METRICS_EXPLORER_SAVED_VIEWS }],
  });

  const [updateSavedViewOrderingToAllAccountManagers] = useNorthbeamMutation<
    UpdateMetricsExplorerSavedViewOrderingToAllAccountManagers,
    UpdateMetricsExplorerSavedViewOrderingToAllAccountManagersVariables
  >(UPDATE_METRICS_EXPLORER_SAVED_VIEW_ORDERING_TO_ALL_ACCOUNT_MANAGERS, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_METRICS_EXPLORER_SAVED_VIEWS }],
  });

  const saveSavedViewsOrdering = useCallback(async () => {
    try {
      setIsSavedViewsReorderLoading(true);
      const savedSavedViewOrder = savedViews.map((id) => id);
      if (!isEqual(currentSavedViewOrder, savedSavedViewOrder)) {
        await updateSavedViewOrdering({
          variables: { ids: currentSavedViewOrder.map((e) => e.id) },
        });
      }
    } finally {
      setIsSavedViewsReorderLoading(false);
    }
  }, [currentSavedViewOrder, savedViews, updateSavedViewOrdering]);

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = currentSavedViewOrder.findIndex(
        ({ id }) => id === active.id,
      );
      const newIndex = currentSavedViewOrder.findIndex(
        ({ id }) => id === over?.id,
      );
      const newOrdering = arrayMove(currentSavedViewOrder, oldIndex, newIndex);

      prettyLogEventPage({
        domain: "Metrics Explorer",
        action: "Other",
        additionalActionInfo: "Reorder Saved Views",
        data: {
          newSavedViews: newOrdering,
          pageContext: { savedViews },
        },
      });

      setCurrentSavedViewOrder(newOrdering);
    }
  };

  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    if (!isMenuOpen) {
      saveSavedViewsOrdering();
    }
  }, [isReordering, isMenuOpen, saveSavedViewsOrdering]);

  const backFromReordering = async () => {
    try {
      await saveSavedViewsOrdering();
    } finally {
      cancelReordering();
    }
  };

  return (
    <div className="relative">
      <div className="px-4 mt-1 mb-2">
        <IconButton
          className="text-cosmic text-xs font-bold px-0"
          leftIcon={<i className="fa-regular fa-sharp fa-arrow-left" />}
          onClick={backFromReordering}
        >
          BACK
        </IconButton>
        <SyncViewReordering
          updateSavedViewOrderingToAllAccountManagers={async () => {
            await updateSavedViewOrderingToAllAccountManagers({
              variables: { ids: currentSavedViewOrder.map((e) => e.id) },
            });
          }}
        />
      </div>
      <div
        className={clsx("max-h-[300px] overflow-y-auto", {
          "pointer-events-none": isSavedViewsReorderLoading,
        })}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext items={savedViews}>
            {currentSavedViewOrder.map((savedView) => (
              <SavedViewsMenuRow
                key={savedView.id}
                savedView={savedView}
                isReordering
                isSelected={savedView.id === selectedSavedView?.id}
                isTemplate={savedView.is_template}
                onClick={() => {
                  selectSavedView(savedView);
                  closeMenu();
                }}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
      {isSavedViewsReorderLoading && (
        <div className="top-0 right-0 bottom-0 left-0">
          <div className="absolute-center">
            <Spinner colorClassname="text-cosmic" size="lg" />
          </div>
        </div>
      )}
    </div>
  );
};
